import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import DashboardLayout from "../components/DashboardLayout";
import Billing from "./Billing";
import Invoices from "./Invoices";
import PhotoManager from "../components/PhotoManager";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Calendar, Coffee, Clock, CheckCircle, Plus, Edit2, Trash2, AlertCircle, Eye } from "lucide-react";

function StatCard({ label, value, accent, icon: Icon }) {
  return (
    <div data-testid={`stat-${label}`} className="stat-card">
      <div className="flex items-start justify-between">
        <div className="overline text-[11px]">{label}</div>
        {Icon && <Icon size={16} className="text-[#C5A059]" />}
      </div>
      <div className={`mt-4 font-serif text-4xl ${accent ? "text-[#C5A059]" : "text-[#F5F0EB]"}`}>{value}</div>
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/cafe").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="p-8 text-[#A8A39D]">Chargement...</div>;

  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">{t("dash.overview")}</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light mb-12">Bonjour ☕</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label={t("dash.totalReservations")} value={stats.total_reservations} accent icon={Calendar} />
        <StatCard label={t("dash.pending")} value={stats.pending_reservations} icon={Clock} />
        <StatCard label={t("dash.confirmed")} value={stats.confirmed_reservations} icon={CheckCircle} />
        <StatCard label="Vues du mois" value={stats.views_month ?? 0} icon={Eye} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div data-testid="reservations-chart" className="lg:col-span-2 stat-card">
          <div className="overline mb-6">{t("dash.reservations14d")}</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.reservations_series}>
              <CartesianGrid stroke="rgba(245,240,235,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#A8A39D" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#A8A39D" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141210", border: "1px solid rgba(197,160,89,0.3)", borderRadius: 0 }} labelStyle={{ color: "#C5A059" }} />
              <Line type="monotone" dataKey="count" stroke="#C5A059" strokeWidth={2} dot={{ fill: "#C5A059", r: 3 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div data-testid="upcoming" className="stat-card">
          <div className="overline mb-6">{t("dash.upcoming")}</div>
          {!stats.upcoming.length && <div className="text-[#A8A39D] text-sm py-4">—</div>}
          <div className="divide-y divide-white/5">
            {stats.upcoming.map((r) => (
              <div key={r.id} className="py-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">{r.customer_name}</div>
                  <div className="text-xs uppercase tracking-widest text-[#C5A059]">{r.status}</div>
                </div>
                <div className="text-xs text-[#A8A39D] mt-1">{r.date} · {r.time} · {r.party_size} pers.</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductForm({ initial, onCancel, onSave }) {
  const { t } = useI18n();
  const [form, setForm] = useState(initial || { name: "", name_en: "", description: "", description_en: "", price: 0, category: "Café", image_url: "", available: true });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div data-testid="product-form" className="stat-card mb-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="pf-name" className="luxe-input" placeholder="Nom (FR)" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <input data-testid="pf-name-en" className="luxe-input" placeholder="Name (EN)" value={form.name_en} onChange={(e) => set("name_en", e.target.value)} />
        <input data-testid="pf-desc" className="luxe-input" placeholder="Description (FR)" value={form.description} onChange={(e) => set("description", e.target.value)} />
        <input data-testid="pf-desc-en" className="luxe-input" placeholder="Description (EN)" value={form.description_en} onChange={(e) => set("description_en", e.target.value)} />
        <input data-testid="pf-price" type="number" step="0.10" className="luxe-input" placeholder="Prix $" value={form.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
        <input data-testid="pf-category" className="luxe-input" placeholder="Catégorie" value={form.category} onChange={(e) => set("category", e.target.value)} />
        <input data-testid="pf-image" className="luxe-input sm:col-span-2" placeholder="Image URL" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
      </div>
      <div className="mt-6 flex gap-3">
        <button data-testid="pf-save" onClick={() => onSave(form)} className="btn-gold">{t("dash.save")}</button>
        <button data-testid="pf-cancel" onClick={onCancel} className="btn-ghost">{t("dash.cancel")}</button>
      </div>
    </div>
  );
}

function Menu() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => { const { data } = await api.get("/products"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) await api.put(`/products/${editing.id}`, form);
      else await api.post("/products", form);
      setEditing(null); setAdding(false);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/products/${id}`);
    await load();
  };

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline mb-2">{t("dash.menu")}</div>
          <h1 className="font-serif text-4xl font-light">{list.length} {list.length > 1 ? "items" : "item"}</h1>
        </div>
        {!adding && !editing && (
          <button data-testid="add-product-btn" onClick={() => setAdding(true)} className="btn-gold"><Plus size={14} /> {t("dash.addProduct")}</button>
        )}
      </div>

      {(adding || editing) && <ProductForm initial={editing} onCancel={() => { setAdding(false); setEditing(null); }} onSave={save} />}

      <div className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">Nom</th><th>{t("dash.category")}</th><th>{t("dash.price")}</th><th className="text-right">{t("dash.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} data-testid={`row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 object-cover" />}
                    <span>{p.name}</span>
                  </div>
                </td>
                <td className="text-[#A8A39D]">{p.category}</td>
                <td className="text-[#C5A059] font-serif text-lg">{p.price.toFixed(2)} $</td>
                <td className="text-right">
                  <button data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-[#A8A39D] hover:text-[#C5A059]"><Edit2 size={14} /></button>
                  <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="p-2 text-[#A8A39D] hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={4} className="py-10 text-center text-[#A8A39D]">{t("dash.noProducts")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reservations() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = async () => {
    const params = filter !== "all" ? { status_filter: filter } : {};
    const { data } = await api.get("/reservations", { params });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (id, status) => {
    await api.patch(`/reservations/${id}/status`, { status });
    await load();
  };

  const filters = [
    { v: "all", l: "Toutes" }, { v: "pending", l: "En attente" }, { v: "confirmed", l: "Confirmées" },
    { v: "completed", l: "Terminées" }, { v: "cancelled", l: "Annulées" }, { v: "no_show", l: "No show" },
  ];

  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">{t("dash.reservations")}</div>
      <h1 className="font-serif text-4xl font-light mb-8">{list.length} {t("dash.reservations").toLowerCase()}</h1>

      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {filters.map((f) => (
          <button key={f.v} data-testid={`filter-${f.v}`} onClick={() => setFilter(f.v)} className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border whitespace-nowrap transition-colors ${filter === f.v ? "bg-[#C5A059] text-[#0A0908] border-[#C5A059]" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"}`}>{f.l}</button>
        ))}
      </div>

      <div className="grid gap-4">
        {list.map((r) => (
          <div key={r.id} data-testid={`reservation-${r.id}`} className="stat-card grid md:grid-cols-[1fr_auto_auto] gap-6 items-center">
            <div>
              <div className="text-xs text-[#A8A39D] tracking-widest uppercase">{r.date} · {r.time}</div>
              <div className="font-serif text-xl mt-1">{r.customer_name}</div>
              <div className="text-xs text-[#A8A39D] mt-1">
                {r.party_size} {r.party_size > 1 ? "personnes" : "personne"} · {r.customer_phone}
                {r.customer_email ? ` · ${r.customer_email}` : ""}
              </div>
              {r.notes && <div className="text-xs text-[#A8A39D] mt-2 italic">"{r.notes}"</div>}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[#C5A059]">{r.status}</div>
            <select data-testid={`r-status-${r.id}`} value={r.status} onChange={(e) => update(r.id, e.target.value)} className="bg-[#1C1916] border border-white/10 px-3 py-2 text-xs uppercase tracking-widest text-[#F5F0EB]">
              <option value="pending">pending</option>
              <option value="confirmed">confirmed</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
              <option value="no_show">no_show</option>
            </select>
          </div>
        ))}
        {!list.length && <div data-testid="no-reservations" className="stat-card text-center text-[#A8A39D] py-12">{t("dash.noReservations")}</div>}
      </div>
    </div>
  );
}

const ALL_SERVICES = ["Wifi", "Terrasse", "Sans gluten", "Végan", "Animaux acceptés", "Stationnement", "Accessible PMR", "Réservation"];
const CATEGORIES = ["Café", "Boulangerie", "Pâtisserie", "Salon de thé", "Café-restaurant"];

function CafeSettings() {
  const { t } = useI18n();
  const [cafe, setCafe] = useState(null);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/me/cafe").then((r) => setCafe({ ...r.data, hours: r.data.hours || { mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" }, services: r.data.services || [] })); }, []);

  const save = async () => {
    setErr("");
    try {
      const res = await api.put("/me/cafe", {
        name: cafe.name, description: cafe.description, description_en: cafe.description_en,
        category: cafe.category, address: cafe.address, city: cafe.city, province: cafe.province, postal_code: cafe.postal_code,
        country: cafe.country || "Canada",
        phone: cafe.phone, email: cafe.email,
        logo_url: cafe.logo_url, banner_url: cafe.banner_url,
        services: cafe.services, hours: cafe.hours,
        accepts_reservations: cafe.accepts_reservations,
      });
      // Refresh geocoded coordinates from the server
      setCafe((c) => ({ ...c, latitude: res.data.latitude, longitude: res.data.longitude }));
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const toggleService = (s) => {
    setCafe((c) => ({ ...c, services: c.services.includes(s) ? c.services.filter((x) => x !== s) : [...c.services, s] }));
  };

  if (!cafe) return <div className="p-8 text-[#A8A39D]">...</div>;
  const DAYS = [["mon","Lun"],["tue","Mar"],["wed","Mer"],["thu","Jeu"],["fri","Ven"],["sat","Sam"],["sun","Dim"]];

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="overline mb-2">{t("dash.settings")}</div>
      <h1 className="font-serif text-4xl font-light mb-10">{cafe.name}</h1>

      <div className="space-y-6">
        <div className="stat-card space-y-5">
          <div className="overline mb-1">Identité</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="overline text-xs">Nom</label><input data-testid="cs-name" className="luxe-input mt-2" value={cafe.name || ""} onChange={(e) => setCafe({ ...cafe, name: e.target.value })} /></div>
            <div>
              <label className="overline text-xs">Catégorie</label>
              <select data-testid="cs-category" className="luxe-input mt-2" value={cafe.category || "Café"} onChange={(e) => setCafe({ ...cafe, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="overline text-xs">Description FR</label><textarea data-testid="cs-desc" rows={3} className="luxe-input mt-2" value={cafe.description || ""} onChange={(e) => setCafe({ ...cafe, description: e.target.value })} /></div>
          <div><label className="overline text-xs">Description EN</label><textarea data-testid="cs-desc-en" rows={3} className="luxe-input mt-2" value={cafe.description_en || ""} onChange={(e) => setCafe({ ...cafe, description_en: e.target.value })} /></div>
        </div>

        <div className="stat-card space-y-5">
          <div className="overline mb-1">Adresse (géolocalisation automatique)</div>
          <div><label className="overline text-xs">Adresse</label><input data-testid="cs-address" className="luxe-input mt-2" value={cafe.address || ""} onChange={(e) => setCafe({ ...cafe, address: e.target.value })} placeholder="123 rue Saint-Denis" /></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="overline text-xs">Ville</label><input data-testid="cs-city" className="luxe-input mt-2" value={cafe.city || ""} onChange={(e) => setCafe({ ...cafe, city: e.target.value })} placeholder="Montréal" /></div>
            <div><label className="overline text-xs">Province / État</label><input data-testid="cs-province" className="luxe-input mt-2" value={cafe.province || ""} onChange={(e) => setCafe({ ...cafe, province: e.target.value })} placeholder="QC / NY" /></div>
            <div><label className="overline text-xs">Code postal / ZIP</label><input data-testid="cs-postal" className="luxe-input mt-2" value={cafe.postal_code || ""} onChange={(e) => setCafe({ ...cafe, postal_code: e.target.value })} placeholder="H2X 1Y2 / 10001" /></div>
          </div>
          <div><label className="overline text-xs">Pays</label>
            <select data-testid="cs-country" className="luxe-input mt-2" value={cafe.country || "Canada"} onChange={(e) => setCafe({ ...cafe, country: e.target.value })}>
              <option>Canada</option>
              <option>United States</option>
            </select>
          </div>
          {cafe.latitude != null && cafe.longitude != null ? (
            <div data-testid="cs-geo-detected" className="mt-3 px-3 py-2 bg-[#C5A059]/10 border border-[#C5A059]/30 text-xs text-[#C5A059] tracking-wider flex items-center gap-2">
              <span>📍</span>
              <span>Position détectée automatiquement : {cafe.latitude.toFixed(5)}, {cafe.longitude.toFixed(5)} — visible sur la carte</span>
            </div>
          ) : (
            <div className="mt-3 px-3 py-2 border border-white/10 text-xs text-[#A8A39D]">
              La position GPS sera détectée automatiquement à l'enregistrement à partir de votre adresse.
            </div>
          )}
        </div>

        <div className="stat-card space-y-5">
          <div className="overline mb-1">Contact</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="overline text-xs">Téléphone</label><input data-testid="cs-phone" className="luxe-input mt-2" value={cafe.phone || ""} onChange={(e) => setCafe({ ...cafe, phone: e.target.value })} placeholder="514-555-0000" /></div>
            <div><label className="overline text-xs">Courriel</label><input data-testid="cs-email" type="email" className="luxe-input mt-2" value={cafe.email || ""} onChange={(e) => setCafe({ ...cafe, email: e.target.value })} /></div>
          </div>
          <div className="text-[11px] text-[#A8A39D] tracking-wider">Aucun champ « site web externe » — toute la visibilité reste sur cafe-spot.ca.</div>
        </div>

        <div className="stat-card space-y-5">
          <div className="overline mb-1">Visuels</div>
          <div><label className="overline text-xs">Bannière URL</label><input data-testid="cs-banner" className="luxe-input mt-2" value={cafe.banner_url || ""} onChange={(e) => setCafe({ ...cafe, banner_url: e.target.value })} /></div>
          <div><label className="overline text-xs">Logo URL</label><input data-testid="cs-logo" className="luxe-input mt-2" value={cafe.logo_url || ""} onChange={(e) => setCafe({ ...cafe, logo_url: e.target.value })} /></div>
        </div>

        <div className="stat-card space-y-4">
          <div className="overline mb-1">Horaires d'ouverture</div>
          <div className="space-y-2">
            {DAYS.map(([k, label]) => (
              <div key={k} className="grid grid-cols-[80px_1fr] gap-4 items-center">
                <div className="text-[11px] uppercase tracking-widest text-[#A8A39D]">{label}</div>
                <input data-testid={`cs-h-${k}`} className="luxe-input !py-1" placeholder="08:00-18:00 ou vide pour fermé" value={cafe.hours?.[k] || ""} onChange={(e) => setCafe({ ...cafe, hours: { ...cafe.hours, [k]: e.target.value } })} />
              </div>
            ))}
          </div>
        </div>

        <div className="stat-card">
          <div className="overline mb-4">Services proposés</div>
          <div className="flex flex-wrap gap-2">
            {ALL_SERVICES.map((s) => (
              <button key={s} data-testid={`svc-${s}`} type="button" onClick={() => toggleService(s)} className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${cafe.services.includes(s) ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="stat-card flex items-center justify-between">
          <div>
            <div className="overline mb-1">Réservations en ligne</div>
            <div className="text-sm text-[#A8A39D]">Accepter les demandes de réservation via CAFÉ-SPOT</div>
          </div>
          <button data-testid="cs-toggle-res" onClick={() => setCafe({ ...cafe, accepts_reservations: !cafe.accepts_reservations })} className={`px-5 py-2 text-xs tracking-widest uppercase border transition-colors ${cafe.accepts_reservations ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D]"}`}>
            {cafe.accepts_reservations ? "Activé" : "Désactivé"}
          </button>
        </div>

        <PhotoManager cafe={cafe} onChange={(patch) => setCafe((c) => ({ ...c, ...patch }))} />

        {err && <div className="text-red-400 text-sm">{err}</div>}

        <div className="flex items-center gap-4">
          <button data-testid="cs-save" onClick={save} className="btn-gold">{t("dash.save")}</button>
          {saved && <span className="text-[#C5A059] text-sm tracking-widest uppercase">✓ Enregistré</span>}
          <span className="text-xs text-[#A8A39D] ml-auto">Fiche publique : <span className="text-[#C5A059]">/cafe/{cafe.slug}</span></span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#A8A39D]">...</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/dashboard" }} />;
  if (user.role !== "cafe_owner") return <Navigate to="/" />;

  return (
    <DashboardLayout>
      {!user.email_verified && (
        <div data-testid="email-not-verified-banner" className="bg-orange-400/10 border-b border-orange-400/30 px-8 py-3 text-sm text-orange-300 flex items-center gap-3">
          <AlertCircle size={16} />
          <span>Votre courriel n'est pas encore confirmé. Certaines fonctionnalités sont limitées tant que vous n'avez pas confirmé.</span>
        </div>
      )}
      <Routes>
        <Route index element={<Overview />} />
        <Route path="billing" element={<Billing />} />
        <Route path="menu" element={<Menu />} />
        <Route path="reservations" element={<Reservations />} />
        <Route path="settings" element={<CafeSettings />} />
      </Routes>
    </DashboardLayout>
  );
}
